'use client';

// ============================================================
// PageRetouche : aperçu plein écran et retouche d'une page scannée
// ============================================================
// Le traitement automatique fait l'essentiel, mais un justificatif comptable ne
// supporte pas l'à-peu-près et personne ne juge mieux que celui qui tient le
// papier. Cet écran lui rend la main : redresser, recadrer, éclaircir.
//
// Deux principes tirés de la mise au point :
//  - on ne travaille JAMAIS sur l'image blanchie. Le blanchiment sature les
//    niveaux et rend l'inclinaison impossible à mesurer (-4,4 deg avant, 0
//    après). La source est l'image redressée non blanchie ; le blanchiment
//    n'est qu'un réglage appliqué à la sortie.
//  - un curseur seul est trop imprécis au doigt : on double d'un pas-à-pas.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowCounterClockwise as RotateLeft, ArrowClockwise as RotateRight,
  Check, MagicWand, CircleNotch as Loader2, Warning,
} from '@phosphor-icons/react';
import { angleLignes, blanchir, diagnostiquerPage, type DiagnosticPage } from '@/lib/document-flatten';
import { loadImage } from './scan-stages';
import { haptic } from '@/lib/portal-ux';

export interface ReglagesPage {
  angle: number;      // inclinaison fine, en degrés
  quart: number;      // rotations d'un quart de tour
  marge: number;      // en %, négatif pour élargir, positif pour resserrer
  blanc: 0 | 1 | 2;
}

export const REGLAGES_DEFAUT: ReglagesPage = { angle: 0, quart: 0, marge: 0, blanc: 1 };

type Onglet = 'droiture' | 'cadre' | 'rendu';

export function PageRetouche({
  numero, source, reglagesInitiaux, onAnnuler, onAppliquer, onRefaire,
}: {
  numero: number;
  source: string;                       // dataURI de l'image NON blanchie
  reglagesInitiaux?: ReglagesPage;
  onAnnuler: () => void;
  onAppliquer: (finale: string, reglages: ReglagesPage) => void;
  onRefaire?: () => void;
}) {
  const [reglages, setReglages] = useState<ReglagesPage>(reglagesInitiaux ?? REGLAGES_DEFAUT);
  const [onglet, setOnglet] = useState<Onglet>('droiture');
  const [message, setMessage] = useState<string | null>(null);
  const [diag, setDiag] = useState<DiagnosticPage | null>(null);
  const [prete, setPrete] = useState(false);
  const toileRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const avantBlanchimentRef = useRef<HTMLCanvasElement | null>(null);

  // Rendu : rotation, marge, puis blanchiment. On garde la version non blanchie,
  // c'est elle qui sert à mesurer l'inclinaison.
  const rendre = useCallback((r: ReglagesPage): HTMLCanvasElement | null => {
    const im = imageRef.current;
    const toile = toileRef.current;
    if (!im || !toile) return null;

    const rot = ((r.angle + r.quart * 90) * Math.PI) / 180;
    const sw = im.naturalWidth, sh = im.naturalHeight;
    const ca = Math.abs(Math.cos(rot)), sa = Math.abs(Math.sin(rot));
    let w = Math.ceil(sw * ca + sh * sa), h = Math.ceil(sw * sa + sh * ca);
    const ech = Math.min(1, 1500 / Math.max(w, h));
    w = Math.max(20, Math.round(w * ech));
    h = Math.max(20, Math.round(h * ech));

    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    ctx.fillStyle = '#ffffff';           // les coins découverts par la rotation sont du papier
    ctx.fillRect(0, 0, w, h);
    ctx.translate(w / 2, h / 2);
    ctx.rotate(rot);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(im, (-sw * ech) / 2, (-sh * ech) / 2, sw * ech, sh * ech);

    let base = c;
    const m = r.marge / 100;
    if (Math.abs(m) > 0.001) {
      const dx = Math.round(w * m), dy = Math.round(h * m);
      const nw = Math.max(20, w - 2 * dx), nh = Math.max(20, h - 2 * dy);
      const c2 = document.createElement('canvas');
      c2.width = nw; c2.height = nh;
      const x2 = c2.getContext('2d', { willReadFrequently: true })!;
      x2.fillStyle = '#ffffff';
      x2.fillRect(0, 0, nw, nh);
      x2.drawImage(c, dx, dy, w - 2 * dx, h - 2 * dy, 0, 0, nw, nh);
      base = c2;
    }
    avantBlanchimentRef.current = base;

    const affiche = document.createElement('canvas');
    affiche.width = base.width; affiche.height = base.height;
    affiche.getContext('2d')!.drawImage(base, 0, 0);
    blanchir(affiche, r.blanc);

    toile.width = affiche.width;
    toile.height = affiche.height;
    toile.getContext('2d')!.drawImage(affiche, 0, 0);
    return affiche;
  }, []);

  useEffect(() => {
    let annule = false;
    (async () => {
      const im = await loadImage(source);
      if (annule || !im) return;
      imageRef.current = im;
      rendre(reglagesInitiaux ?? REGLAGES_DEFAUT);
      setPrete(true);
      const base = avantBlanchimentRef.current;
      if (base) {
        const d = diagnostiquerPage(base);
        setDiag(d);
      }
    })();
    return () => { annule = true; };
    // Volontairement une seule fois : la source ne change pas pour un écran donné.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const appliquerReglages = useCallback((maj: Partial<ReglagesPage>) => {
    setReglages((r) => {
      const suivant = { ...r, ...maj };
      rendre(suivant);
      return suivant;
    });
  }, [rendre]);

  const redresserAuto = useCallback(() => {
    const base = avantBlanchimentRef.current;
    if (!base) return;
    const a = angleLignes(base);
    if (!a) { setMessage('Aucune inclinaison nette détectée'); return; }
    const suivant = { ...reglages, angle: Math.max(-15, Math.min(15, reglages.angle + a)) };
    setReglages(suivant);
    rendre(suivant);
    haptic('tap');
    const reste = avantBlanchimentRef.current ? angleLignes(avantBlanchimentRef.current) : 0;
    setMessage('Redressé de ' + a.toFixed(1) + '°' + (Math.abs(reste) > 0.2 ? ', reste ' + reste.toFixed(1) + '°' : ''));
    setDiag((d) => (d ? { ...d, angle: reste, soucis: d.soucis.filter((s) => !s.startsWith('texte penché')) } : d));
  }, [reglages, rendre]);

  const valider = useCallback(() => {
    const rendu = rendre(reglages);
    if (!rendu) return;
    haptic('success');
    onAppliquer(rendu.toDataURL('image/jpeg', 0.9), reglages);
  }, [reglages, rendre, onAppliquer]);

  const boutonOnglet = (id: Onglet, libelle: string) => (
    <button
      type="button"
      onClick={() => setOnglet(id)}
      className={`flex-shrink-0 rounded-[10px] border px-3.5 py-2 text-[12.5px] transition-all ${
        onglet === id
          ? 'border-[#8B5CF6] bg-[#8B5CF6] font-semibold text-white'
          : 'border-[#2A2D35] bg-[#1E2028] text-[#8A8F98]'
      }`}
    >
      {libelle}
    </button>
  );

  const petitBouton = (libelle: string, onClick: () => void, actif = false) => (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-[38px] rounded-[10px] border bg-[#1E2028] px-3.5 text-[12.5px] transition-all active:scale-95 ${
        actif ? 'border-[#8B5CF6] text-[#A78BFA]' : 'border-[#2A2D35] text-[#E8E9EB]'
      }`}
    >
      {libelle}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#08090B]">
      <div
        className="flex flex-shrink-0 items-center justify-between border-b border-[#1F2127] px-4 pb-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)' }}
      >
        <button type="button" onClick={onAnnuler} className="inline-flex items-center gap-1.5 text-[13px] text-[#8A8F98] active:scale-95">
          <ArrowLeft size={16} /> Annuler
        </button>
        <span className="text-[15px] font-semibold text-[#E8E9EB]">Page {numero}</span>
        <button
          type="button"
          onClick={() => { setReglages(REGLAGES_DEFAUT); rendre(REGLAGES_DEFAUT); setMessage('Réglages remis à zéro'); }}
          className="text-[13px] text-[#8A8F98] active:scale-95"
        >
          Réinitialiser
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-black">
        <canvas ref={toileRef} className="max-h-full max-w-full" />
        {!prete && (
          <div className="absolute inset-0 flex items-center justify-center text-[#8A8F98]">
            <Loader2 size={22} className="animate-spin" />
          </div>
        )}
        {message && (
          <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 whitespace-nowrap rounded-full border border-[#2A2D35] bg-black/70 px-3 py-1.5 text-[11.5px] text-[#E8E9EB] backdrop-blur">
            {message}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-[#1F2127] bg-[#0B0C0E]">
        {diag && diag.soucis.length > 0 && (
          <div className="mx-4 mb-1 mt-3 flex items-center gap-2.5 rounded-xl border border-[#B45309]/50 bg-[#B45309]/15 px-3 py-2.5">
            <Warning size={17} className="flex-shrink-0 text-[#FDBA74]" />
            <p className="m-0 flex-1 text-[12px] leading-snug text-[#FDBA74]">
              Cette page semble de travers : {diag.soucis.join(', ')}.
            </p>
            <button
              type="button"
              onClick={redresserAuto}
              className="flex-shrink-0 rounded-[9px] bg-[#B45309] px-3 py-2 text-[12px] font-semibold text-white active:scale-95"
            >
              Corriger
            </button>
          </div>
        )}

        {/* La zone doit se lire comme une boîte à outils, pas comme une barre de
            navigation : on la nomme. */}
        <div className="flex items-center gap-2 px-4 pb-0.5 pt-3 text-[11.5px] font-bold uppercase tracking-wider text-[#A78BFA]">
          <span aria-hidden className="h-[2px] w-4 rounded bg-[#A78BFA] shadow-[0_5px_0_#A78BFA,0_-5px_0_#A78BFA]" />
          Retoucher cette page
        </div>

        <div className="flex gap-1.5 overflow-x-auto px-3 pt-2.5">
          {boutonOnglet('droiture', 'Redresser')}
          {boutonOnglet('cadre', 'Recadrer')}
          {boutonOnglet('rendu', 'Rendu')}
        </div>

        <div className="px-4 pb-3 pt-3.5">
          {onglet === 'droiture' && (
            <>
              <div className="flex items-center gap-3">
                <label htmlFor="rt-angle" className="flex-shrink-0 text-[12.5px] text-[#8A8F98]">Inclinaison</label>
                <input
                  id="rt-angle" type="range" min={-15} max={15} step={0.1} value={reglages.angle}
                  onChange={(e) => appliquerReglages({ angle: Number(e.target.value) })}
                  className="flex-1 accent-[#8B5CF6]"
                />
                <span className="min-w-[54px] text-right text-[12.5px] tabular-nums text-[#E8E9EB]">
                  {reglages.angle.toFixed(1).replace('.', ',')}°
                </span>
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {petitBouton('-1°', () => appliquerReglages({ angle: Math.max(-15, reglages.angle - 1) }))}
                {petitBouton('-0,2', () => appliquerReglages({ angle: Math.max(-15, reglages.angle - 0.2) }))}
                {petitBouton('+0,2', () => appliquerReglages({ angle: Math.min(15, reglages.angle + 0.2) }))}
                {petitBouton('+1°', () => appliquerReglages({ angle: Math.min(15, reglages.angle + 1) }))}
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={redresserAuto}
                  className="inline-flex min-h-[38px] items-center gap-1.5 rounded-[10px] border border-[#2A2D35] bg-[#1E2028] px-3.5 text-[12.5px] text-[#E8E9EB] active:scale-95"
                >
                  <MagicWand size={15} /> Redresser automatiquement
                </button>
                <button
                  type="button"
                  onClick={() => appliquerReglages({ quart: (reglages.quart + 3) % 4 })}
                  aria-label="Pivoter à gauche"
                  className="inline-flex min-h-[38px] w-[46px] items-center justify-center rounded-[10px] border border-[#2A2D35] bg-[#1E2028] text-[#E8E9EB] active:scale-95"
                >
                  <RotateLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => appliquerReglages({ quart: (reglages.quart + 1) % 4 })}
                  aria-label="Pivoter à droite"
                  className="inline-flex min-h-[38px] w-[46px] items-center justify-center rounded-[10px] border border-[#2A2D35] bg-[#1E2028] text-[#E8E9EB] active:scale-95"
                >
                  <RotateRight size={16} />
                </button>
              </div>
            </>
          )}

          {onglet === 'cadre' && (
            <>
              <div className="flex items-center gap-3">
                <label htmlFor="rt-marge" className="flex-shrink-0 text-[12.5px] text-[#8A8F98]">Marge</label>
                <input
                  id="rt-marge" type="range" min={-4} max={8} step={0.5} value={reglages.marge}
                  onChange={(e) => appliquerReglages({ marge: Number(e.target.value) })}
                  className="flex-1 accent-[#8B5CF6]"
                />
                <span className="min-w-[54px] text-right text-[12.5px] tabular-nums text-[#E8E9EB]">
                  {reglages.marge.toFixed(1).replace('.', ',')} %
                </span>
              </div>
              <p className="mt-2.5 text-[11.5px] leading-snug text-[#8A8F98]">
                Vers la gauche on élargit pour ne rien couper, vers la droite on resserre pour retirer un reste de fond.
              </p>
            </>
          )}

          {onglet === 'rendu' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-[12.5px] text-[#8A8F98]">Blanchiment</span>
              {petitBouton('Aucun', () => appliquerReglages({ blanc: 0 }), reglages.blanc === 0)}
              {petitBouton('Doux', () => appliquerReglages({ blanc: 1 }), reglages.blanc === 1)}
              {petitBouton('Fort', () => appliquerReglages({ blanc: 2 }), reglages.blanc === 2)}
            </div>
          )}
        </div>

        <div
          className="flex gap-2.5 px-4 pt-1"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)' }}
        >
          {onRefaire && (
            <button
              type="button"
              onClick={onRefaire}
              className="min-h-[50px] flex-1 rounded-xl border border-[#2A2D35] bg-[#1E2028] text-[13px] font-medium text-[#E8E9EB] active:scale-[0.98]"
            >
              Refaire la photo
            </button>
          )}
          <button
            type="button"
            onClick={valider}
            disabled={!prete}
            className="inline-flex min-h-[50px] flex-[2] items-center justify-center gap-2 rounded-xl text-[14.5px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50"
            style={{ background: 'linear-gradient(180deg, #8B5CF6, #7C3AED)' }}
          >
            <Check size={18} /> Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}
